import { useState } from 'react';
import { Modal, Button, useToast } from '@invested/ui';
import * as api from '../../services/api';
import type { ProjectStatus } from '../../types';
import { PROJECT_STATUS_META, PROJECT_STATUS_ORDER } from '../../lib/statusMeta';

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'o')
    .replace(/å/g, 'a')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 120);
}

export function NewProjectModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState<ProjectStatus>('active');
  const [currentStage, setCurrentStage] = useState('');
  const [copyablePrompt, setCopyablePrompt] = useState('');
  const [pending, setPending] = useState(false);

  const effectiveSlug = slugTouched ? slug : slugify(name);

  async function submit() {
    if (!name.trim() || !category.trim() || effectiveSlug.length < 2) {
      toast.warning('Navn, kategori og slug må fylles ut.');
      return;
    }
    setPending(true);
    try {
      await api.createProject({
        name: name.trim(),
        slug: effectiveSlug,
        category: category.trim(),
        status,
        currentStage: currentStage.trim() || null,
        copyablePrompt: copyablePrompt.trim() || null,
      });
      toast.success('Prosjekt opprettet.');
      onCreated();
    } catch (e) {
      toast.danger(e instanceof api.ApiError ? e.message : 'Kunne ikke opprette prosjekt.');
    } finally {
      setPending(false);
    }
  }

  const field = 'w-full rounded-xl border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-black/30';

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      title="Nytt prosjekt"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Avbryt
          </Button>
          <Button onClick={() => void submit()} disabled={pending}>
            {pending ? 'Oppretter…' : 'Opprett'}
          </Button>
        </div>
      }
    >
      <div className="space-y-3 text-[#0f172a]">
        <label className="block text-sm font-medium">
          Navn
          <input className={`mt-1 ${field}`} value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="block text-sm font-medium">
          Slug
          <input
            className={`mt-1 font-mono ${field}`}
            value={effectiveSlug}
            onChange={(e) => {
              setSlugTouched(true);
              setSlug(e.target.value);
            }}
          />
        </label>
        <label className="block text-sm font-medium">
          Kategori
          <input className={`mt-1 ${field}`} value={category} onChange={(e) => setCategory(e.target.value)} />
        </label>
        <label className="block text-sm font-medium">
          Status
          <select className={`mt-1 ${field}`} value={status} onChange={(e) => setStatus(e.target.value as ProjectStatus)}>
            {PROJECT_STATUS_ORDER.map((s) => (
              <option key={s} value={s}>
                {PROJECT_STATUS_META[s].label}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm font-medium">
          Nåværende steg
          <input className={`mt-1 ${field}`} value={currentStage} onChange={(e) => setCurrentStage(e.target.value)} />
        </label>
        <label className="block text-sm font-medium">
          Kopierbar prompt
          <textarea
            className={`mt-1 h-24 ${field}`}
            value={copyablePrompt}
            onChange={(e) => setCopyablePrompt(e.target.value)}
          />
        </label>
      </div>
    </Modal>
  );
}
